import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/primitives/AlertDialog";
import { Badge } from "@/components/primitives";
import { UserCheck, UserX, Mail, Calendar } from "lucide-react";

interface PendingInvitation {
	id: string;
	mentor_id: string;
	mentor_name: string;
	mentor_email: string;
	invited_at: string;
	invitation_message?: string;
}

interface MentorRelationship {
	id: string;
	mentor_id: string;
	status: string;
	created_at: string;
	mentor: {
		name: string;
		email: string;
	} | null;
}

export function MentorInvitationDialog() {
	const { user, profile } = useAuth();
	const [pendingInvitations, setPendingInvitations] = useState<
		PendingInvitation[]
	>([]);
	const [currentInvitation, setCurrentInvitation] =
		useState<PendingInvitation | null>(null);
	const [isOpen, setIsOpen] = useState(false);
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (profile?.user_type === "mentee") {
			checkPendingInvitations();
		}
	}, [profile]);

	const checkPendingInvitations = async () => {
		if (!user?.id) return;

		try {
			//console.log("🔍 Checking for pending invitations for mentee:", user.id);

			// Query the correct table: mentor_mentee_relationships with mentor profile join
			const { data, error } = await supabase
				.from("mentor_mentee_relationships")
				.select(`
          id,
          mentor_id,
          status,
          created_at,
          mentor:profiles!mentor_id(name, email)
        `)
				.eq("mentee_id", user.id)
				.eq("status", "pending");

			if (error) {
				console.error("Error fetching pending invitations:", error);
				return;
			}

			//console.log("📋 Found pending invitations:", data);

			if (data && data.length > 0) {
				const formattedInvitations: PendingInvitation[] = (data as any[]).map(
					(invitation: any) => ({
						id: invitation.id,
						mentor_id: invitation.mentor_id,
						mentor_name: invitation.mentor?.name || "Unknown Mentor",
						mentor_email: invitation.mentor?.email || "",
						invited_at: invitation.created_at,
						invitation_message: undefined,
					}),
				);

				setPendingInvitations(formattedInvitations);
				setCurrentInvitation(formattedInvitations[0]);
				setIsOpen(true);
				//console.log("✅ Showing invitation dialog for:", formattedInvitations[0].mentor_name);
			} else {
				//console.log("📭 No pending invitations found");
			}
		} catch (error) {
			console.error("Error checking invitations:", error);
		}
	};

	const handleResponse = async (accept: boolean) => {
		if (!currentInvitation) return;

		setLoading(true);
		try {
			//console.log(`🔄 Responding to invitation: ${accept ? 'accept' : 'decline'}`);

			const { error } = await supabase
				.from("mentor_mentee_relationships")
				.update({
					status: accept ? "accepted" : "declined",
					// Note: removed responded_at since it may not exist in current schema
				})
				.eq("id", currentInvitation.id);

			if (error) {
				console.error("Error responding to invitation:", error);
				alert("Error responding to invitation. Please try again.");
				return;
			}

			//console.log("✅ Successfully updated invitation status");

			// Remove the responded invitation from the list
			const remaining = pendingInvitations.filter(
				(inv) => inv.id !== currentInvitation.id,
			);
			setPendingInvitations(remaining);

			// Show next invitation if any
			if (remaining.length > 0) {
				setCurrentInvitation(remaining[0]);
				//console.log("📝 Showing next invitation");
			} else {
				setCurrentInvitation(null);
				setIsOpen(false);
				//console.log("✅ All invitations processed");
			}

			// Show success message
			const action = accept ? "accepted" : "declined";
			const message = accept
				? `You are now connected with ${currentInvitation.mentor_name} as your mentor!`
				: `You have declined the invitation from ${currentInvitation.mentor_name}.`;

			alert(message);
			//console.log(`Mentor invitation ${action} successfully`);
		} catch (error) {
			//console.error("Error responding to invitation:", error);
			alert("Error responding to invitation. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handleSkip = () => {
		// Move to next invitation or close dialog
		const remaining = pendingInvitations.filter(
			(inv) => inv.id !== currentInvitation?.id,
		);
		if (remaining.length > 0) {
			setCurrentInvitation(remaining[0]);
		} else {
			setIsOpen(false);
		}
	};

	if (!currentInvitation || profile?.user_type !== "mentee") {
		return null;
	}

	return (
		<AlertDialog open={isOpen} onOpenChange={setIsOpen}>
			<AlertDialogContent className="max-w-md">
				<AlertDialogHeader>
					<div className="flex items-center space-x-3 mb-2">
						<div className="p-2 bg-blue-100 rounded-full">
							<UserCheck className="w-6 h-6 text-blue-600" />
						</div>
						<div>
							<AlertDialogTitle className="text-lg">
								Mentor Invitation
							</AlertDialogTitle>
							{pendingInvitations.length > 1 && (
								<Badge variant="secondary" className="text-xs">
									{pendingInvitations.indexOf(currentInvitation) + 1} of{" "}
									{pendingInvitations.length}
								</Badge>
							)}
						</div>
					</div>
					<AlertDialogDescription className="text-left">
						<strong>{currentInvitation.mentor_name}</strong> has invited you to
						be their mentee.
					</AlertDialogDescription>
				</AlertDialogHeader>

				<div className="px-6 space-y-4">
					<div className="bg-gray-50 rounded-lg p-3 space-y-2">
						<div className="flex items-center space-x-2 text-sm">
							<Mail className="w-4 h-4 text-gray-500" />
							<span className="text-gray-600">
								{currentInvitation.mentor_email}
							</span>
						</div>
						<div className="flex items-center space-x-2 text-sm">
							<Calendar className="w-4 h-4 text-gray-500" />
							<span className="text-gray-600">
								Invited{" "}
								{new Date(currentInvitation.invited_at).toLocaleDateString()}
							</span>
						</div>
					</div>

					<div className="bg-blue-50 border-l-4 border-blue-200 p-3">
						<p className="text-sm text-blue-800 italic">
							"I'd like to invite you to be my mentee. I'm here to help guide
							your reflection journey."
						</p>
					</div>

					<p className="text-sm text-gray-600">
						As their mentee, they will be able to view any reflections they set
						in general check in analysis for whole group. They won't be able to
						view any other reflections or your individual check ins.
					</p>
				</div>

				<AlertDialogHeader className="hidden"></AlertDialogHeader>

				<AlertDialogFooter className="flex-col sm:flex-row gap-2">
					<div className="flex gap-2 w-full">
						<AlertDialogCancel
							onClick={() => handleResponse(false)}
							disabled={loading}
							className="flex-1 bg-red-50 border-red-200 text-red-700 hover:bg-red-100"
						>
							<UserX className="w-4 h-4 mr-2" />
							Decline
						</AlertDialogCancel>

						<AlertDialogAction
							onClick={() => handleResponse(true)}
							disabled={loading}
							className="flex-1 bg-green-600 hover:bg-green-700"
						>
							<UserCheck className="w-4 h-4 mr-2" />
							{loading ? "Processing..." : "Accept"}
						</AlertDialogAction>
					</div>

					{pendingInvitations.length > 1 && (
						<button
							onClick={handleSkip}
							className="text-sm text-gray-500 hover:text-gray-700 mt-2"
						>
							Review later
						</button>
					)}
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}
